import { isThisMonth, isThisWeek } from 'date-fns';
import {
  Fragment,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import { DetailsModalContext, UserContext } from '../../../../context';
import {
  ChannelNotificationsPayload,
  NotificationComment,
  NotificationDefault,
  NotificationLeaderBoard,
  NotificationMention,
  NotificationType,
} from '../../../../services';
import { getChannelNotifications, loadMoreChannelNotifications } from '../../../../slices';
import { NOTIFICATIONS_STORAGE_KEY } from '../../../constants';
import { useAnalytics, useAppDispatch, useAppSelector, useIsNarrowViewport } from '../../../hooks';
import { getCssVar, setCssVar } from '../../../utils';
import { Popup } from '../../Popup';
import { TabItem, Tabs } from '../../Tabs';
import { Comment } from '../Comment';
import { Default } from '../Default';
import { LeaderBoard } from '../LeaderBoard';
import { Mention } from '../Mention';

import classNames from 'classnames';
import classes from './NotificationsCommon.module.scss';

export enum NotificationsTabType {
  VIEW_ALL,
  FOLLOWING,
  GENERAL,
}

interface RenderNotificationArgs {
  notification:
    | NotificationDefault
    | NotificationComment
    | NotificationMention
    | NotificationLeaderBoard;
}

export const NotificationsCommon: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const location = useLocation();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const { logPushOpen } = useAnalytics();

  const isNarrowViewport = useIsNarrowViewport();

  const { channelNotifications, page, hasNextPage, isFetched } = useAppSelector(
    ({ channelNotifications }) => channelNotifications
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [selectedTabIndex, setSelectedTabIndex] = useState(NotificationsTabType.VIEW_ALL);

  const [unreadNotificationsTime, setUnreadNotificationsTime] = useState<string[]>([]);

  const storageKey = useMemo(() => `${NOTIFICATIONS_STORAGE_KEY}_${channelId}`, [channelId]);

  const type = useMemo(() => {
    switch (selectedTabIndex) {
      case NotificationsTabType.GENERAL:
        return [NotificationType.DEFAULT];
      case NotificationsTabType.FOLLOWING:
        return [NotificationType.COMMENT, NotificationType.STORY_CARD_MENTION];
      default:
        return [
          NotificationType.DEFAULT,
          NotificationType.COMMENT,
          NotificationType.STORY_CARD_MENTION,
          NotificationType.LEADER_BOARD_USER_CLOSE_TO_PROMOTION,
          NotificationType.LEADER_BOARD_USER_PROMOTED_TO_NEW_BADGE,
          NotificationType.REFERRAL_USER_ACTIVATED,
        ];
    }
  }, [selectedTabIndex]);

  const getNotifications = useCallback(async () => {
    const { items }: ChannelNotificationsPayload = await dispatch(
      getChannelNotifications({ channelId, type })
    ).unwrap();

    const lastNotificationTime = localStorage.getItem(storageKey);

    const notificationsTime = items.map(({ deliveredTime }) => deliveredTime);

    if (!lastNotificationTime) {
      setUnreadNotificationsTime(notificationsTime);
      return;
    }

    setUnreadNotificationsTime(
      notificationsTime.filter((dateStr) => new Date(dateStr) > new Date(lastNotificationTime))
    );
  }, [channelId, dispatch, storageKey, type]);

  useEffect(() => {
    getNotifications();
  }, [getNotifications, selectedTabIndex]);

  useEffect(() => {
    setIsOpen(false);
    setDetailsModalData({ storyId: null, cardId: null });
  }, [location, setDetailsModalData]);

  useEffect(() => {
    const unreadCount = unreadNotificationsTime.length;

    if (unreadCount) {
      setCssVar('--notifications-unread-count', unreadCount > 9 ? '"9+"' : `"${unreadCount}"`);
    }
  }, [unreadNotificationsTime.length]);

  const loadMoreNotifications = useCallback(() => {
    dispatch(loadMoreChannelNotifications({ channelId, type, page: page + 1 }));
  }, [channelId, dispatch, page, type]);

  const iconClickHandler = useCallback(() => {
    setIsOpen(!isOpen);

    if (unreadNotificationsTime[0]) {
      localStorage.setItem(storageKey, unreadNotificationsTime[0]);
    }

    setUnreadNotificationsTime([]);
  }, [isOpen, storageKey, unreadNotificationsTime]);

  const logOpen = useCallback(
    (notification_id: number) =>
      logPushOpen({ channel_id: channelId, notification_id, time_stamp: new Date().toISOString() }),
    [channelId, logPushOpen]
  );

  const getGroupLabel = useCallback(
    (time: string) => {
      switch (true) {
        case isThisWeek(new Date(time), { weekStartsOn: 1 }):
          return t('channelNotifications.this-week');
        case isThisMonth(new Date(time)):
          return t('channelNotifications.this-month');
        default:
          return t('channelNotifications.earlier');
      }
    },
    [t]
  );

  const renderNotification = useCallback(
    ({ notification }: RenderNotificationArgs) => {
      const props = { setIsOpen, logOpen };

      switch (notification.type) {
        case NotificationType.DEFAULT:
          return <Default notification={notification as NotificationDefault} {...props} />;
        case NotificationType.COMMENT:
          return <Comment notification={notification as NotificationComment} {...props} />;
        case NotificationType.STORY_CARD_MENTION:
          return <Mention notification={notification as NotificationMention} {...props} />;
        case NotificationType.LEADER_BOARD_USER_CLOSE_TO_PROMOTION:
        case NotificationType.LEADER_BOARD_USER_PROMOTED_TO_NEW_BADGE:
        case NotificationType.REFERRAL_USER_ACTIVATED:
          return <LeaderBoard notification={notification as NotificationLeaderBoard} {...props} />;
        default:
          return null;
      }
    },
    [logOpen]
  );

  const renderNotifications = useMemo(() => {
    return (
      <>
        {channelNotifications.map((notification, index, notificationsArray) => {
          const { id, deliveredTime } = notification;

          return (
            <Fragment key={id}>
              {getGroupLabel(notificationsArray[index - 1]?.deliveredTime) !==
                getGroupLabel(deliveredTime) && (
                <div className={classes['notifications__group-label']}>
                  {getGroupLabel(deliveredTime)}
                </div>
              )}
              {renderNotification({ notification })}
            </Fragment>
          );
        })}
      </>
    );
  }, [channelNotifications, getGroupLabel, renderNotification]);

  const loader = useMemo(() => <Skeleton className={classes['notifications__loader']} />, []);

  const tabContent = useMemo(() => {
    if (!isFetched) {
      return loader;
    }

    if (!channelNotifications.length) {
      return (
        <div className={classes['notifications__no-content']}>
          {t('channelNotifications.no-content')}
        </div>
      );
    }

    return (
      <InfiniteScroll
        className={classes['notifications__infinite-scroll']}
        height={'30rem'} //needed for infinite scroll inside modal
        next={loadMoreNotifications}
        hasMore={hasNextPage}
        loader={loader}
        dataLength={channelNotifications.length}
      >
        {renderNotifications}
      </InfiniteScroll>
    );
  }, [
    channelNotifications.length,
    hasNextPage,
    isFetched,
    loadMoreNotifications,
    loader,
    renderNotifications,
    t,
  ]);

  const tabItems: TabItem[] = useMemo(() => {
    return Object.values(NotificationsTabType)
      .filter((values) => typeof values === 'string')
      .map((value) => {
        return {
          index: NotificationsTabType[value as keyof typeof NotificationsTabType],
          name: t(`channelNotificationsTabs.${value}`),
          content: tabContent,
        };
      });
  }, [t, tabContent]);

  const popupBody = useMemo(() => {
    return (
      <div
        className={classNames(classes['notifications'], {
          [classes['notifications--mobile']]: isMobileOnly,
        })}
      >
        <div className={classes['notifications__header']}>
          <span className={classes['notifications__header-title']}>
            {t('channelNotifications.title')}
          </span>
        </div>
        <Tabs
          items={tabItems}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          noBackground
        />
      </div>
    );
  }, [selectedTabIndex, t, tabItems]);

  return (
    <Popup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      iconId={'bell'}
      ariaLabel={t('notifications.title')}
      color={getCssVar('--notifications-icon-color')}
      hoverColor={getCssVar('--notifications-icon-hover-color')}
      onClick={iconClickHandler}
      bodyTop={'2rem'}
      bodyRight={'0'}
      {...(isNarrowViewport && {
        bodyTop: 'var(--navbar-height)',
        bodyRight: '1rem',
        bodyPosition: 'fixed',
      })}
      body={popupBody}
      className={classNames({
        [classes['notifications__icon--has-new']]: Boolean(unreadNotificationsTime.length),
      })}
    />
  );
};
